import { useContext, useState } from 'react';
import {
  IContact,
  IMeeting,
  IMeetingsMeetingType,
  useContactsQuery,
  useCreateMeetingMutation,
  useMeetingsMeetingTypeQuery,
  useUpdateMeetingMutation
} from '../../generated/graphql';
import { AppContext } from '../../AppContext';
import { getNullableDateString, parseDate } from '../../utils/clientUtils';
import DialogBox from '../Common/Layout/DialogBox';
import { Box, Chip, InputBase } from '@mui/material';
import EditButton from '../Common/Buttons/EditButton';
import { Colors } from '../../utils/theme';
import InputComponent from '../Common/Fields/InputComponent';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import EpraTimePicker from '../Common/Fields/EpraTimePicker';
import { Info } from '@mui/icons-material';
import React from 'react';
import { Actions } from '../../enums/ActionEnums';
import { Editor } from '@tinymce/tinymce-react';
import moment from 'moment';

const defaultErrorsObj: IErrors = {
  name: null,
  meetingType: null,
  date: null,
  startTime: null,
  endTime: null
};

type IErrors = {
  name: string | null;
  meetingType: string | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
};

const errorTexts = {
  name: 'Name is required!',
  meetingType: 'Meeting Type is required!',
  date: 'Select date',
  startTime: 'Select start time',
  startTimeInPast: 'Selected time should not be in the past',
  endTime: 'Select end time',
  endTimeInPast: 'Selected time should not be in the past',
  endTimeBeforeStart: 'The end time should not be before start time'
};

type IProps = {
  handleClose: () => void;
  meetingInput: IMeeting | null;
  contactId?: number;
  updateList?: (meeting: IMeeting) => void;
  editList: (meeting: IMeeting) => void;
};

const NewMeeting: React.FunctionComponent<IProps> = ({
  handleClose,
  meetingInput,
  contactId,
  updateList,
  editList
}: IProps) => {
  const { dispatch } = useContext(AppContext);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const meetingTypes = useMeetingsMeetingTypeQuery();
  // Use cache-first to avoid refetching all contacts if already cached
  const contactsOptions = useContactsQuery({
    fetchPolicy: 'cache-first'
  });
  const [editable, setEditable] = useState(meetingInput === null);
  const [name, setName] = useState<string>(meetingInput?.name || '');
  const [subject, setSubject] = useState<string | null>(meetingInput?.subject || null);
  const [meetingType, setMeetingType] = useState<IMeetingsMeetingType | null>(
    meetingInput?.meeting_type || null
  );
  const [meetingDetails, setMeetingDetails] = useState<string | null>(
    meetingInput?.meeting_details || null
  );
  const [location, setLocation] = useState<string | null>(meetingInput?.location || null);
  const [date, setDate] = useState<Date | null>(parseDate(meetingInput?.start_time) || null);
  const [startTime, setStartTime] = useState<Date | null>(
    parseDate(meetingInput?.start_time) || null
  );
  const [endTime, setEndTime] = useState<Date | null>(parseDate(meetingInput?.end_time) || null);
  const [contacts, setContacts] = useState<IContact[]>(
    (meetingInput?.contacts as IContact[]) || []
  );
  const [createMeeting, createMeetingResult] = useCreateMeetingMutation();
  const [updateMeeting, updateMeetingResult] = useUpdateMeetingMutation();

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value && value.length > 0) {
      setFormErrors({ ...formErrors, name: null });
    } else {
      setFormErrors({ ...formErrors, name: errorTexts.name });
    }
    setName(value);
  };

  const handleDateChange = (dateInput: Date | null) => {
    if (dateInput === null) {
      setFormErrors({ ...formErrors, date: errorTexts.date });
    }
    setDate(dateInput);
  };

  const handleStartTimeChange = (timeInput: Date | null) => {
    if (timeInput !== null) {
      setFormErrors({ ...formErrors, startTime: null });
    }
    setStartTime(timeInput);
  };

  const handleEndTimeChange = (timeInput: Date | null) => {
    if (timeInput !== null) {
      setFormErrors({ ...formErrors, endTime: null });
      if (date) {
        if (startTime) {
          if (moment(timeInput).isSameOrBefore(startTime)) {
            setFormErrors({ ...formErrors, endTime: errorTexts.endTimeBeforeStart });
          }
        }
      } else {
        setFormErrors({ ...formErrors, endTime: errorTexts.endTimeInPast });
      }
    }
    setEndTime(timeInput);
  };

  const handleSubjectChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    setSubject(value);
  };

  const handleLocationChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    setLocation(value);
  };

  const handleMeetingTypeChange = (
    _: unknown,
    newValue: React.SetStateAction<IMeetingsMeetingType | null>
  ) => {
    setFormErrors({
      ...formErrors,
      meetingType: newValue === null ? errorTexts.meetingType : null
    });
    setMeetingType(newValue);
  };

  const handleContactsChange = (_: unknown, contacts: IContact[]) => {
    setContacts(contacts);
  };

  const takeContactIds = (contacts: IContact[]) => {
    const contactIds = contacts.map((c) => c.id);
    return contactIds;
  };

  const handleSave = () => {
    let hasErrors = false;
    const errs: IErrors = {
      name: null,
      meetingType: null,
      date: null,
      startTime: null,
      endTime: null
    };
    if (name.length < 2) {
      hasErrors = true;
      errs.name = errorTexts.name;
    }
    if (meetingType === null) {
      hasErrors = true;
      errs.meetingType = errorTexts.meetingType;
    }
    if (date === null) {
      hasErrors = true;
      errs.date = errorTexts.date;
    }
    if (startTime === null) {
      hasErrors = true;
      errs.startTime = errorTexts.startTime;
    }
    if (endTime === null) {
      hasErrors = true;
      errs.endTime = errorTexts.endTime;
    }
    if (hasErrors) {
      setFormErrors(errs);
    } else {
      sumbitMeeting();
    }
  };

  const sumbitMeeting = () => {
    const meetingStartDateTime = new Date(
      Date.UTC(
        (date as Date).getFullYear(),
        (date as Date).getMonth(),
        (date as Date).getDate(),
        (startTime as Date).getHours(),
        (startTime as Date).getMinutes(),
        0,
        0
      )
    );
    const meetingEndDateTime = new Date(
      Date.UTC(
        (date as Date).getFullYear(),
        (date as Date).getMonth(),
        (date as Date).getDate(),
        (endTime as Date).getHours(),
        (endTime as Date).getMinutes(),
        0,
        0
      )
    );
    const input: any = {
      name: name as string,
      subject: subject as string,
      meeting_type_id: Number(meetingType?.id as string),
      meeting_details: meetingDetails as string,
      location: location as string,
      start_time: getNullableDateString(meetingStartDateTime),
      end_time: getNullableDateString(meetingEndDateTime),
      contacts: contacts.map((c) => Number(c.id))
    };
    // Only include contact_id if contactId is provided (for contacts tab)
    if (contactId !== undefined) {
      input.contact_id = contactId;
    }
    if (meetingInput !== null) {
      updateMeeting({
        variables: {
          id: (meetingInput as IMeeting).id,
          input
        },
        update(_, { data }) {
          if (data?.result) {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `You have successfully updated ${data.result.name}'s meeting data`
              }
            });
            handleClose();
            editList(data?.result as IMeeting);
          }
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    } else {
      createMeeting({
        variables: {
          input
        },
        update(_, { data }) {
          if (data?.result) {
            // Only call updateList if provided (for components without subscriptions)
            if (updateList) {
              updateList(data.result as IMeeting);
            }
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `Meeting ${input.name} created`
              }
            });
            handleClose();
          }
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="contact-meeting-form"
      data-testid="contact-meeting-form"
      title={meetingInput ? 'Meeting Details' : 'Create Meeting'}
      width={1400}
      height={940}
      onClose={handleClose}
    >
      {meetingInput && (
        <Box sx={{ position: 'absolute', right: '24px' }}>
          <EditButton
            id="contact-meeting-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', mt: '46px', px: '3.93%', gap: '3.78%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '45px', width: '100%' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: '66px 22px',
              padding: '22px 22px'
            }}
          >
            <InputComponent
              id="contact-meeting-form-name"
              label="Meeting Name"
              value={name || ''}
              onChange={handleNameChange}
              errorMsg={formErrors.name}
              additionalStyle={{ gridColumn: 'span 2' }}
              disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
            />
            <InputComponent
              id="contact-meeting-form-subject"
              label="Subject"
              value={subject || ''}
              onChange={handleSubjectChange}
              additionalStyle={{ gridColumn: 'span 2' }}
              disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
            />
            <AutocompleteComponent
              id="contact-meeting-form-type"
              label="Meeting Type"
              options={(meetingTypes.data?.result as IMeetingsMeetingType[]) ?? []}
              getOptionLabel={(option) => option.name}
              values={meetingType}
              onChange={handleMeetingTypeChange}
              errorMsg={formErrors.meetingType}
              additionalStyle={{ gridColumn: 'span 2' }}
              disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
            />
            <InputComponent
              id="contact-meeting-form-location"
              label="Location"
              value={location || ''}
              onChange={handleLocationChange}
              additionalStyle={{ gridColumn: 'span 2' }}
              disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="contact-meeting-form-date"
                label="Date"
                value={date}
                format={'dd/MM/yyyy'}
                onChange={(d) => handleDateChange(d)}
                disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
                errorMsg={formErrors.date}
                sx={{ gridColumn: 'span 2' }}
              />
              <EpraTimePicker
                id="contact-meeting-form-start-time"
                label="Start Time"
                value={startTime}
                onChange={(t) => handleStartTimeChange(t)}
                disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
                errorMsg={formErrors.startTime}
                sx={{
                  gridColumn: 'span 3'
                }}
              />
              <InputBase
                id="contact-meeting-form-start-time-info"
                data-testid="contact-meeting-form-start-time-info"
                value={`Based on UTC`}
                disabled
                fullWidth
                sx={{
                  gridColumn: 'span 1'
                }}
                startAdornment={
                  <Info
                    sx={{ width: '17px', height: '17px', marginTop: '-3px', paddingRight: '3px' }}
                  />
                }
                inputProps={{
                  style: {
                    fontSize: '12px',
                    fontFamily: 'Overpass'
                  }
                }}
              />
              <EpraTimePicker
                id="contact-meeting-form-end-time"
                label="End Time"
                value={endTime}
                onChange={(t) => handleEndTimeChange(t)}
                disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
                errorMsg={formErrors.endTime}
                sx={{
                  gridColumn: 'span 3'
                }}
              />
              <InputBase
                id="contact-meeting-form-end-time-info"
                data-testid="contact-meeting-form-end-time-info"
                value={`Based on UTC`}
                disabled
                sx={{
                  gridColumn: 'span 1'
                }}
                fullWidth
                startAdornment={
                  <Info
                    sx={{ width: '17px', height: '17px', marginTop: '-3px', paddingRight: '3px' }}
                  />
                }
                inputProps={{
                  style: {
                    fontSize: '12px',
                    fontFamily: 'Overpass'
                  }
                }}
              />
            </LocalizationProvider>
            <AutocompleteComponent
              id="contact-meeting-form-contacts"
              label="Contacts"
              options={
                (contactsOptions.data?.result.filter(
                  (c) => !takeContactIds(contacts).includes(c?.id as string)
                ) as never[]) || []
              }
              values={contacts.filter((c) => contactId === undefined || Number(c.id) !== contactId)}
              isSingle={false}
              onChange={handleContactsChange}
              getOptionLabel={(option: IContact) => option.fullName}
              disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
              additionalStyle={{ gridColumn: 'span 4' }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  // eslint-disable-next-line react/jsx-key
                  <Chip
                    sx={{
                      backgroundColor: Colors.white,
                      color: Colors.main,
                      '& .MuiChip-deleteIcon': {
                        width: '20px',
                        height: '20px',
                        color: Colors.skyBlue,
                        '&:hover': {
                          color: Colors.main
                        }
                      }
                    }}
                    variant="outlined"
                    label={`${option.fullName}`}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Box>
        </Box>
        <Box sx={{ width: '100%', padding: '22px 22px' }}>
          <Editor
            apiKey={'t5l2zdfj93i0j7jhpf2arppkijlh4uu5a21w93j5i8d4ehkf'}
            disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
            initialValue={meetingInput?.meeting_details ?? ''}
            value={meetingDetails || ''}
            onEditorChange={(newValue) => setMeetingDetails(newValue)}
            init={{
              plugins: 'link image preview imagetools code ',
              toolbar:
                'undo redo | bold italic underline | fontselect | alignleft aligncenter alignright | image link | preview',
              visual: false
            }}
          />
        </Box>
      </Box>
      {editable && (
        <Box className="centralize" sx={{ gridColumn: 'span 3', mt: '40px', mb: '20px' }}>
          <CancelButton
            id="cancel-document-form-button"
            onClick={handleClose}
            disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
            text={meetingInput === null || editable ? 'Cancel' : 'Close'}
          />
          <SaveButton
            id="save-document-form-button"
            onClick={handleSave}
            disabled={!editable || createMeetingResult.loading || updateMeetingResult.loading}
            text={meetingInput === null ? 'Create' : 'Save'}
          />
        </Box>
      )}
    </DialogBox>
  );
};

export default NewMeeting;
